import { Injectable, inject } from '@angular/core';
import { SurveyMeasurementStatuses } from '@core/enums/survey-measurement-statuses';
import { DuplicatedCmlLabelError } from '@core/errors/duplicated-cml-label-error';
import { MeasurementHelper } from '@core/helpers/measurement.helper';
import { SqlitePorterHelper } from '@core/helpers/sqlite-porter.helper';
import { SurveyFileHelper } from '@core/helpers/survey-file.helper';
import { AlarmConfig } from '@core/interfaces/alarm-config';
import { SurveyMeasurement } from '@core/interfaces/survey-measurement';
import { SurveyPoint } from '@core/interfaces/survey-point';
import { DatabaseService } from '@core/services/database.service';
import { ActionRequest, QueueService } from '@core/services/queue.service';
import { UuidService } from '@core/services/uuid.service';
import { SurveyIds } from '@orm-entities/survey-ids';
import { SurveyPoints } from '@orm-entities/survey-points';
import { Observable, concat, concatMap, first, firstValueFrom, from, last, map, of, switchMap, throwError } from 'rxjs';
import { FindOptionsWhere } from 'typeorm';
import { QueryDeepPartialEntity } from 'typeorm';

@Injectable({
  providedIn: 'root',
})
export class CorrosionMonitoringLocationsService {
  readonly #databaseService = inject(DatabaseService);
  readonly #queueService = inject(QueueService);
  readonly #uuidService = inject(UuidService);

  public updateCML(fileId: string, cml: SurveyPoint, taskId: string, alarmConfig: AlarmConfig): Observable<SurveyPoint> {
    const normalizedCML = SurveyFileHelper.setMeasurementToZeroForNullValue(cml);
    MeasurementHelper.setMeasurementStatus(normalizedCML, alarmConfig);
    if (new Set(normalizedCML.ids.map(({ id }) => id)).size !== normalizedCML.ids.length) {
      return throwError(() => 'Cannot set the same measurement ids for the same CML');
    }

    return this.#hasDuplicateLabelsOrEmptyLabel(fileId, normalizedCML.label, normalizedCML.id).pipe(
      switchMap(isDuplicate => {
        if (isDuplicate) return throwError(() => new DuplicatedCmlLabelError());
        return this.#selectExistingMeasurements(fileId).pipe(first());
      }),
      switchMap(existingMeasurements => {
        const existingMeasurementIds = new Set(existingMeasurements.map(id => id.measurementPointId));
        const toUpdate = normalizedCML.ids.filter(({ measurementPointId }) => existingMeasurementIds.has(measurementPointId));
        const toInsert = normalizedCML.ids.filter(({ measurementPointId }) => !existingMeasurementIds.has(measurementPointId));

        return concat(
          this.#updateCMLRow(fileId, normalizedCML),
          this.#updateExistingMeasurements(fileId, normalizedCML.label, toUpdate),
          this.#addNewMeasurements(fileId, normalizedCML.label, toInsert),
        ).pipe(last(null, undefined));
      }),
      switchMap(() => this.#enqueueUpdateAction(fileId, taskId, normalizedCML)),
    );
  }

  public addCML(fileId: string, cml: SurveyPoint, taskId: string): Observable<SurveyPoint> {
    const newCml: SurveyPoint = { ...cml, id: this.#uuidService.generate() };
    return this.#insertCMLRow(fileId, newCml).pipe(
      switchMap(() => this.#enqueueAddAction(fileId, taskId, newCml)),
      map(() => ({ ...newCml, isCustom: true })),
    );
  }

  public deleteCML(cmlId: string, taskId: string): Observable<void> {
    return this.#databaseService.select(SurveyPoints, { id: cmlId }).pipe(
      first(),
      switchMap(cmls => {
        const cml = cmls[0];
        return concat(this.#deleteCMLRow(cmlId, cml.label), this.#deleteCMLMeasurements(cml.fileId, cml.label)).pipe(last(null, undefined));
      }),
      switchMap(() => this.#enqueueDeleteAction(cmlId, taskId)),
      map(() => undefined),
    );
  }

  public editMeasurementComment(
    request: { cmlId: string; measurementId: string; comment: string; status?: string },
    taskId: string,
  ): Observable<void> {
    const updateData: QueryDeepPartialEntity<SurveyIds> = { comment: request.comment, status: request?.status };
    const whereCondition: FindOptionsWhere<SurveyIds> = { measurementPointId: request.measurementId };
    return this.#databaseService.update(SurveyIds, updateData, whereCondition).pipe(
      switchMap(() => this.#enqueueEditCommentAction(request, taskId)),
      map(() => undefined),
    );
  }

  public deleteMeasurement(measurementId: string, taskId: string): Observable<void> {
    return this.#databaseService.delete(SurveyIds, { measurementPointId: measurementId }).pipe(
      switchMap(() => this.#enqueueDeleteMeasurementAction(measurementId, taskId)),
      map(() => undefined),
    );
  }

  public updateCMLStatus(where: { label: string }, status: string): Observable<void> {
    const updateData: QueryDeepPartialEntity<SurveyPoints> = { measurementStatus: status };
    const whereCondition: FindOptionsWhere<SurveyPoints> = { label: where.label };
    return from(firstValueFrom(this.#databaseService.update(SurveyPoints, updateData, whereCondition)).then(() => undefined));
  }

  #hasDuplicateLabelsOrEmptyLabel(fileId: string, label: string, cmlId?: string): Observable<boolean> {
    return this.#databaseService.select(SurveyPoints, { fileId }).pipe(
      first(),
      map(cmls => cmls.some(cml => cml.label === label && cml.id !== cmlId) && label !== ''),
    );
  }

  #selectExistingMeasurements(fileId: string): Observable<SurveyIds[]> {
    return this.#databaseService.select(SurveyIds, { fileId });
  }

  #updateCMLRow(fileId: string, cml: SurveyPoint): Observable<void> {
    const updateRequest: { set: Partial<SurveyPoints>; where: Partial<SurveyPoints> } = {
      set: {
        label: cml.label,
        measurementStatus: cml.measurementStatus,
        left: `${cml.location.left}`,
        top: `${cml.location.top}`,
        height: cml.location.height,
        width: cml.location.width,
        isCustom: cml.isCustom ? 1 : 0,
      },
      where: { fileId, id: cml.id },
    };
    const stmt = SqlitePorterHelper.buildSqlStatement({ data: { updates: { SurveyPoints: [updateRequest] } } });
    return this.#databaseService.executeSql(stmt, []);
  }

  #updateExistingMeasurements(fileId: string, cmlLabel: string, measurementsToUpdate: SurveyMeasurement[]): Observable<void> {
    if (measurementsToUpdate.length === 0) return of(undefined);
    return from(measurementsToUpdate).pipe(
      concatMap(measurement => {
        const updateRequest = {
          set: {
            id: measurement.id,
            previousMeasurement: measurement.previousMeasurement,
            measurement: measurement.measurement,
            status: measurement.status,
            longitude: measurement.longitude,
            latitude: measurement.latitude,
            serialNumber: measurement.serialNumber,
            measurementPointId: measurement.measurementPointId,
            comment: measurement.comment ?? '',
            label: cmlLabel,
            unitOfMeasurement: measurement.unitOfMeasurement,
            isCustom: measurement.isCustom,
          },
          where: { fileId, measurementPointId: measurement.measurementPointId },
        };

        const stmt = SqlitePorterHelper.buildSqlStatement({ data: { updates: { SurveyIds: [updateRequest] } } });
        return this.#databaseService.executeSql(stmt, []);
      }),
      last(),
    );
  }

  #addNewMeasurements(fileId: string, cmlLabel: string, measurementsToAdd: SurveyMeasurement[]): Observable<void> {
    if (measurementsToAdd.length === 0) return of(undefined);
    const insertRequests = measurementsToAdd.map(measurement => ({
      ...measurement,
      label: cmlLabel,
      fileId,
      comment: measurement.comment ?? '',
    }));
    const insertStatement = SqlitePorterHelper.buildSqlStatement({
      data: { inserts: { SurveyIds: insertRequests } },
    });
    return this.#databaseService.executeSql(insertStatement, []);
  }

  #enqueueUpdateAction(fileId: string, taskId: string, cml: SurveyPoint): Observable<SurveyPoint> {
    const cmlWithFileId = { ...cml, fileId };
    const actionRequest: ActionRequest = {
      actionType: 'UpdateCorrosionMonitoringLocation',
      request: JSON.stringify(cmlWithFileId),
      fileId,
      requestedOnUtc: new Date().toUTCString(),
      taskId,
    };
    return from(this.#queueService.enqueue(actionRequest)).pipe(map(() => cmlWithFileId));
  }

  #insertCMLRow(fileId: string, cml: SurveyPoint): Observable<QueryDeepPartialEntity<SurveyPoints>> {
    const row: QueryDeepPartialEntity<SurveyPoints> = {
      id: cml.id,
      label: cml.label,
      measurementStatus: SurveyMeasurementStatuses.UnAssigned,
      left: `${cml.location.left}`,
      top: `${cml.location.top}`,
      height: cml.location.height,
      width: cml.location.width,
      fileId,
      isCustom: 1,
    };
    return this.#databaseService.insert(SurveyPoints, row);
  }

  #enqueueAddAction(fileId: string, taskId: string, cml: SurveyPoint): Observable<boolean> {
    const actionRequest: ActionRequest = {
      actionType: 'AddCML',
      request: JSON.stringify({
        label: cml.label,
        measurementStatus: cml.measurementStatus,
        isCustom: cml.isCustom,
        fileId,
        location: cml.location,
        ids: cml.ids,
        id: cml.id,
      }),
      taskId,
      requestedOnUtc: new Date().toUTCString(),
    };
    return from(this.#queueService.enqueue(actionRequest));
  }

  #deleteCMLRow(cmlId: string, label: string): Observable<number> {
    return this.#databaseService.delete(SurveyPoints, { id: cmlId, label });
  }

  #deleteCMLMeasurements(fileId: string, label: string): Observable<number> {
    return this.#databaseService.delete(SurveyIds, { label, fileId });
  }

  #enqueueDeleteAction(cmlId: string, taskId: string): Observable<boolean> {
    const actionRequest: ActionRequest = {
      actionType: 'DeleteCML',
      request: JSON.stringify({ id: cmlId }),
      taskId,
      requestedOnUtc: new Date().toUTCString(),
    };
    return from(this.#queueService.enqueue(actionRequest));
  }

  #enqueueEditCommentAction(request: { measurementId: string; comment: string; status?: string }, taskId: string): Observable<boolean> {
    const actionRequest: ActionRequest = {
      actionType: 'UpdateComment',
      request: JSON.stringify({
        pointId: request.measurementId,
        comment: request.comment,
        cMLIdStatus: request.status,
      }),
      taskId,
      requestedOnUtc: new Date().toUTCString(),
    };
    return from(this.#queueService.enqueue(actionRequest));
  }

  #enqueueDeleteMeasurementAction(measurementPointId: string, taskId: string): Observable<boolean> {
    const actionRequest: ActionRequest = {
      actionType: 'DeleteMeasurementId',
      request: JSON.stringify({ id: measurementPointId }),
      taskId,
      requestedOnUtc: new Date().toUTCString(),
    };
    return from(this.#queueService.enqueue(actionRequest));
  }
}
